import { Injectable, inject } from '@angular/core';
import { Observable, of, Subject, throwError } from 'rxjs';
import { map, switchMap, finalize, tap, catchError } from 'rxjs/operators';

import { TimeService, NotificationService, HeaderSort, ArraySort } from '@shared';

import { ProblemInstanceService } from '../problem-instance/problem-instance.service';
import { GeneralTask } from '../../models/general-task.model';
import { SkillRequirement } from '../../models/skill-requirement.model';
import { TaskView } from '../../models/task-view.model';
import { TaskUpdateService } from '../task-update/task-update.service';
import { GeneralTaskUpdate } from '../../models/general-task-update.model';
import { TaskAssigningIndicator } from '../../models/task-assigning-indicator.model';
import { FeasibilityService } from '../feasibility/feasibility.service';
import { TaskFeasibility } from '../../models/task-feasibility.model';
import { SkillLevelService } from '../skill-level/skill-level.service';
import { EmployeeTask } from '../../models/employee-task.model';
import { TaskFeasibilityInfo } from '../../models/task-feasibility-info.model';
import {RosterService} from "../roster/roster.service";

@Injectable({
    providedIn: 'root'
})
export class TaskService {
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly timeService = inject(TimeService);
    private readonly taskUpdateService = inject(TaskUpdateService);
    private readonly skillLevelService = inject(SkillLevelService);
    private readonly feasibilityService = inject(FeasibilityService);
    private readonly notificationService = inject(NotificationService);
    private readonly rosterService = inject(RosterService);

    private generalTaskFeasibility: TaskFeasibility;
    private employeeTaskFeasibility: TaskFeasibility;
    private readonly taskAssigningIndicator$: Subject<TaskAssigningIndicator>;
    private readonly reloadGeneralTasks$: Subject<void>;
    private readonly generalTasksLoading$: Subject<boolean>;

    constructor() {
        this.taskAssigningIndicator$ = new Subject();
        this.reloadGeneralTasks$ = new Subject();
        this.generalTasksLoading$ = new Subject();
    }

    public getEmployeeTasks(employeeWorkHourId: string): Observable<TaskView[]> {
        return this.problemInstanceService.getProblemInstance().pipe(
            switchMap(result => {
                if (this.employeeTaskFeasibility) {
                    return of(result.problemInstance);
                }

                const taskIds = result.problemInstance.emTasksNotPatients.map(task => task.originalTaskId);
                return this.feasibilityService.getFeasibility(taskIds).pipe(
                    map(feasibility => {
                        this.employeeTaskFeasibility = feasibility.taskFeasibility;
                        return result.problemInstance;
                    })
                );
            }),
            map(problemInstance =>
                problemInstance.emTasksNotPatients
                    .filter(task => task.workHourId === employeeWorkHourId)
                    .map(task => {
                        const skillLevels = problemInstance.skillLevels;
                        const taskView = this.mapTaskFromInstance(task, skillLevels);
                        // taskView.feasibilityInfo = this.feasibilityService.getTaskFeasibilityInfo(
                        //     taskView.originalTaskId,
                        //     this.employeeTaskFeasibility,
                        //     true
                        // );
                        return taskView;
                    })
            )
        );
    }

    public getGeneralTasks(withNumberOfEmployees?: boolean): Observable<TaskView[]> {
        this.generalTasksLoading$.next(true);
        return this.problemInstanceService.getProblemInstance().pipe(
            map(result => {
                const skillLevels = result.problemInstance.skillLevels;
                let tasks = result.problemInstance.geTasksNotPatients;

                if (withNumberOfEmployees) {
                    tasks = tasks.filter(task => task.numberOfEmployees > 0);
                }

                return tasks.map(task => {
                    if (task.skillRequirement <= 0) {
                        task.skillRequirement = skillLevels[0].skillLevelId;
                    }
                    return this.mapTaskFromInstance(task, skillLevels, true);
                });
            }),
            switchMap(tasks => {
                if (this.generalTaskFeasibility || tasks.length === 0) {
                    this.mapGeneralTaskFeasibilityInfo(tasks);
                    this.generalTasksLoading$.next(false);
                    return of(tasks);
                }

                const taskIds = tasks.map(task => task.originalTaskId);
                return this.feasibilityService.getFeasibility(taskIds).pipe(
                    map(result => {
                        this.generalTaskFeasibility = result.taskFeasibility;
                        this.mapGeneralTaskFeasibilityInfo(tasks);
                        this.generalTasksLoading$.next(false);
                        return tasks;
                    })
                );
            })
        );
    }

    public sortGeneralTasks<T>(tasks: T[], sortValue: HeaderSort): void {
        ArraySort.sort(
            tasks,
            sortValue.sortBy,
            sortValue.sortAscending,
            sortValue.secondarySortBy,
            sortValue.secondarySortAscending
        );
        ArraySort.sort(tasks, 'generalTaskCapacityError', true);
    }

    public assignGeneralTaskToEmployee(
        employeeId: string,
        workHourId: string,
        taskId: string,
        fromEmployeeId?: string,
        fromWorkHourId?: string
    ): Observable<void> {
        this.taskAssigningIndicator$.next(<TaskAssigningIndicator>{
            workHourId,
            assigning: true,
            text: 'ASSIGNING_TASK_TO_EMPLOYEE'
        });

        return this.problemInstanceService.getProblemInstance().pipe(
            switchMap(result =>
                this.taskUpdateService
                    .updateGeneralTasks(<GeneralTaskUpdate>{
                        assignments: [{ taskId, employeeId, workHourId }],
                        unassignments: fromEmployeeId
                            ? [{ taskId, employeeId: fromEmployeeId, workHourId: fromWorkHourId }]
                            : []
                    })
                    .pipe(
                        switchMap(updateResult => {
                            result.problemInstance.taskWorkShiftCompatibility = updateResult.taskWorkShiftCompatibility;
                            result.problemInstance.officeInfo.numberOfTasksWithoutMainResponsibles =
                                updateResult.numberOfTasksWithoutMainResponsibles;
                            return this.loadFeasibility(result.problemInstance.geTasksNotPatients);
                        }),
                        tap(() => {
                            if (fromEmployeeId) {
                                this.moveGeneralTaskFromEmployeeToEmployee(
                                    employeeId,
                                    workHourId,
                                    fromWorkHourId,
                                    taskId,
                                    result.problemInstance.emTasksNotPatients
                                );
                            } else {
                                this.moveGeneralTaskToEmployee(
                                    employeeId,
                                    workHourId,
                                    result.problemInstance.emTasksNotPatients,
                                    taskId,
                                    result.problemInstance.geTasksNotPatients
                                );
                            }

                            this.notificationService.success('EMPLOYEE_UPDATED');
                        })
                    )
            ),
            catchError(() => throwError('Assign general task to employee failed.')),
            finalize(() => {
                this.taskAssigningIndicator$.next(<TaskAssigningIndicator>{ workHourId, assigning: false });
            })
        );
    }

    public unassignGeneralTaskFromEmployee(
        employeeId: string,
        workHourId: string,
        originalTaskId: string
    ): Observable<void> {
        this.taskAssigningIndicator$.next(<TaskAssigningIndicator>{
            workHourId,
            assigning: true,
            text: 'UNASSIGNING_TASK_FROM_EMPLOYEE'
        });

        return this.problemInstanceService.getProblemInstance().pipe(
            switchMap(result =>
                this.taskUpdateService
                    .updateGeneralTasks(<GeneralTaskUpdate>{
                        unassignments: [{ employeeId, taskId: originalTaskId, workHourId }],
                        assignments: []
                    })
                    .pipe(
                        switchMap(updateResult => {
                            result.problemInstance.taskWorkShiftCompatibility = updateResult.taskWorkShiftCompatibility;
                            result.problemInstance.officeInfo.numberOfTasksWithoutMainResponsibles =
                                updateResult.numberOfTasksWithoutMainResponsibles;
                            return this.loadFeasibility(result.problemInstance.geTasksNotPatients);
                        }),
                        tap(() => {
                            this.removeGeneralTaskFromEmployee(
                                workHourId,
                                originalTaskId,
                                result.problemInstance.emTasksNotPatients,
                                result.problemInstance.geTasksNotPatients
                            );
                            this.notificationService.success('EMPLOYEE_UPDATED');
                        }),
                        catchError(() => throwError('Unassign general task from employee failed.')),
                        finalize(() => {
                            this.taskAssigningIndicator$.next(<TaskAssigningIndicator>{ workHourId, assigning: false });
                        })
                    )
            )
        );
    }

    public reloadGeneralTasks(): void {
        this.generalTaskFeasibility = null;
        this.reloadGeneralTasks$.next();
    }

    public getTaskAssigningIndicator(): Observable<TaskAssigningIndicator> {
        return this.taskAssigningIndicator$.asObservable();
    }

    public getGeneralTasksLoadingStatus(): Observable<boolean> {
        return this.generalTasksLoading$.asObservable();
    }

    public getReloadGeneralTasks(): Observable<void> {
        return this.reloadGeneralTasks$.asObservable();
    }

    public existHeavyTasks(): Observable<boolean> {
        return this.problemInstanceService.getProblemInstance().pipe(
            map(result => {
                const tasks = [
                    ...result.problemInstance.emTasksNotPatients,
                    ...result.problemInstance.geTasksNotPatients,
                    ...result.problemInstance.tasks
                ];
                const existHeavyTask = tasks.findIndex(task => task.isHeavy);
                if (existHeavyTask) {
                    return true;
                }

                return false;
            })
        );
    }

    private loadFeasibility(tasks: GeneralTask[]): Observable<void> {
        const taskIds = tasks.map(task => task.originalTaskId);
        return this.feasibilityService.getFeasibility(taskIds).pipe(
            map(feasibility => {
                this.generalTaskFeasibility = feasibility.taskFeasibility;
            })
        );
    }

    private mapGeneralTaskFeasibilityInfo(tasks: TaskView[]): void {
        if (!this.generalTaskFeasibility || tasks.length === 0) {
            return;
        }

        tasks.forEach(task => {
            const generalTaskCapacityError = this.generalTaskFeasibility.errors.generalTaskCapacityErrors?.find(
                error => error.taskId === task.originalTaskId
            );

            if (generalTaskCapacityError) {
                task.feasibilityInfo.generalTaskCapacityError = true;
                task.feasibilityInfo.numberOfNonAllocatedTasks = generalTaskCapacityError.numberOfNonAllocatedTasks;
            } else if (this.generalTaskFeasibility.errors.syncedCapacityErrors?.length > 0) {
                task.feasibilityInfo.syncedCapacityError = this.generalTaskFeasibility.errors.syncedCapacityErrors.some(
                    error => error.taskId === task.originalTaskId
                );
            } else if (this.generalTaskFeasibility.errors.durationExceedsTimeWindowErrors?.length > 0) {
                task.feasibilityInfo.durationExceedsTimeWindowError =
                    this.generalTaskFeasibility.errors.durationExceedsTimeWindowErrors.some(
                        error => error.taskId === task.originalTaskId
                    );
            }
        });
    }

    private mapTaskFromInstance<T>(task: T, skillLevels: SkillRequirement[], generalTask?: boolean): TaskView {
        const taskView = Object.assign(<TaskView>{}, task);

        if (generalTask) {
            taskView.skillRequirementDescription = this.skillLevelService.getSkillRequirementDescription(
                taskView,
                skillLevels
            );
        } else {
            const skillLevel = skillLevels.find(item => item.skillLevelId === taskView.skillRequirement);
            taskView.skillRequirementDescription = skillLevel ? skillLevel.skillLevelDescription : undefined;
            taskView.isActive = (task as unknown as EmployeeTask).isActive;
            taskView.isShiftSplitTask = (task as unknown as EmployeeTask).isShiftSplitTask;
        }

        taskView.fromTime = this.timeService.convertTime(taskView.fromTime, true);
        taskView.toTime = this.timeService.convertTime(taskView.toTime, true);
        taskView.duration = taskView.duration
            ? taskView.duration
            : this.timeService.timeDifference(taskView.fromTime, taskView.toTime);
        taskView.feasibilityInfo = <TaskFeasibilityInfo>{};
        taskView.rosterDepartmentName = this.rosterService.getRosterNameById(taskView.rosterDepartmentId);
        taskView.editing = false;
        return taskView;
    }

    private moveGeneralTaskToEmployee(
        employeeId: string,
        workHourId: string,
        employeeTasks: EmployeeTask[],
        generalTaskId: string,
        generalTasks: GeneralTask[]
    ): void {
        const index = generalTasks.findIndex(task => task.originalTaskId === generalTaskId);
        if (index > -1) {
            const generalTask = generalTasks[index];
            if (generalTask.numberOfEmployees && generalTask.numberOfEmployees > 1) {
                generalTask.numberOfEmployees--;
            } else {
                generalTasks.splice(index, 1);
            }

            employeeTasks.push({
                employeeId,
                workHourId,
                taskId: `${generalTask.taskId}-emp-${workHourId}`,
                originalTaskId: generalTask.originalTaskId,
                serviceName: generalTask.serviceName,
                fromDate: generalTask.fromDate,
                fromTime: generalTask.fromTime,
                toDate: generalTask.toDate,
                toTime: generalTask.toTime,
                duration: generalTask.duration,
                skillRequirement: generalTask.skillRequirement,
                numberOfHigherSkillLevelsAccepted: generalTask.numberOfHigherSkillLevelsAccepted,
                allowedShiftCodes: generalTask.allowedShiftCodes,
                prohibitedShiftCodes: generalTask.prohibitedShiftCodes,
                isHeavy: generalTask.isHeavy,
                isSyncedInitially: generalTask.isSyncedInitially,
                doesRequirePhysicalAppearance: generalTask.doesRequirePhysicalAppearance,
                fixedToEmployee: false,
                streetAddressNameNumberAndLetter: generalTask.streetAddressNameNumberAndLetter,
                zipCode: generalTask.zipCode,
                rosterDepartmentId: generalTask.rosterDepartmentId,
                addressIsValid: generalTask.addressIsValid,
                isActive: true,
                shiftType: generalTask.shiftType,
                isShiftSplitTask: false
            });
        }
    }

    private moveGeneralTaskFromEmployeeToEmployee(
        employeeId: string,
        workHourId: string,
        fromWorkHourId: string,
        taskId: string,
        employeeTasks: EmployeeTask[]
    ): void {
        const task = employeeTasks.find(i => i.workHourId === fromWorkHourId && i.originalTaskId === taskId);
        if (task) {
            task.employeeId = employeeId;
            task.workHourId = workHourId;
        }
    }

    private removeGeneralTaskFromEmployee(
        workHourId: string,
        originalTaskId: string,
        employeeTasks: EmployeeTask[],
        generalTasks: GeneralTask[]
    ): void {
        const index = employeeTasks.findIndex(
            task => task.workHourId === workHourId && task.originalTaskId === originalTaskId
        );

        if (index > -1) {
            const employeeTask = employeeTasks.splice(index, 1)[0];
            const generalTask = generalTasks.find(task => task.originalTaskId === originalTaskId);
            if (generalTask) {
                generalTask.numberOfEmployees++;
            } else {
                generalTasks.push(<GeneralTask>{
                    taskId: originalTaskId,
                    originalTaskId,
                    serviceName: employeeTask.serviceName,
                    fromDate: employeeTask.fromDate,
                    fromTime: employeeTask.fromTime,
                    toDate: employeeTask.toDate,
                    toTime: employeeTask.toTime,
                    duration: employeeTask.duration,
                    skillRequirement: employeeTask.skillRequirement,
                    numberOfHigherSkillLevelsAccepted: employeeTask.numberOfHigherSkillLevelsAccepted,
                    numberOfEmployees: 1,
                    streetAddressNameNumberAndLetter: employeeTask.streetAddressNameNumberAndLetter,
                    rosterDepartmentId: employeeTask.rosterDepartmentId
                });
            }
        }
    }
}
